import { useSelector } from 'react-redux';
import React, { useState, useEffect } from 'react';

import dayjs from 'dayjs';
import { Modal, Typography, TextField, Button } from 'shared';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';

import { splitPaymentModalStyles } from '../../style/style';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Box, Grid, IconButton, Divider, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';


const SplitPurchaseModal = ({ open, onClose, data, onSplitCallback }) => {
    const { id: companyId } = useSelector((state) => state.company.data);
    const classes = splitPaymentModalStyles();
    const [quantity, setQuantity] = useState('');
    const [rows, setRows] = useState([]);
    const [warningOpen, setWarningOpen] = useState(false);


    useEffect(() => {
        if (data && open) {
            // Initialize with one row containing the original data
            setRows([{ ...data, id: Date.now() }]);
            setQuantity('');
        }
    }, [data, open]);

    const handleSplit = () => {
        const qty = parseInt(quantity);
        if (!qty || qty <= 0) return;

        const totalAmount = parseFloat(data?.purchase_price || 0);
        const splitAmount = (totalAmount / qty).toFixed(2);

        const newRows = Array.from({ length: qty }).map((_, index) => ({
            ...data,
            id: Date.now() + index,
            purchase_price: splitAmount,
        }));

        setRows(newRows);
    };

    const handleAddRow = () => {
        const newCount = rows.length + 1;
        setQuantity(newCount);

        setRows([
            ...rows,
            {
                ...data,
                id: Date.now(),
                purchase_price: 0,
            },
        ]);
    };

    const handleRemoveRow = (id) => {
        setRows(rows.filter((row) => row.id !== id));
    };

    const handleFieldChange = (id, field, value) => {
        setRows(
            rows.map((row) =>
                row.id === id ? { ...row, [field]: value } : row
            )
        );
    };

    const handleSave = () => {
        const totalSplitAmount = rows.reduce((sum, row) => sum + parseFloat(row.purchase_price || 0), 0);
        const originalAmount = parseFloat(data?.purchase_price || 0);

        if (Math.abs(totalSplitAmount - originalAmount) > 0.01) {
            setWarningOpen(true);
        } else {
            finalizeSave();
        }
    };

    const finalizeSave = () => {
        const payload = {
            aggregated_file_inventory_id: data.id,
            company_id: companyId,
            purchaseData: rows.map(row => ({
                purchase_id: row.purchase_id,
                class_1: row.class_1,
                class_2: row.class_2,
                seats: row.seats,
                reference_id: row.reference_id,
                description: row.description,
                venue: row.venue,
                purchase_price: row.purchase_price,
                purchased_at: row.purchased_at,
                expires_at: row.expires_at
            })),
        };
        console.log("this is payload data", payload);
        onSplitCallback(payload);
        onClose();
    };

    const handleConfirmSave = () => {
        setWarningOpen(false);
        finalizeSave();
    };

    const renderHeader = () => (
        <Grid container spacing={1} sx={{
            border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? '#121826' : '#E5E6EB'} `,
            borderRadius: '15px',
            mx: 1, p: 2, boxShadow: '0px 1px 3px rgba(0,0,0,0.1)',
            marginBottom: '20px', backgroundColor: (theme) => (theme.palette.mode === 'dark' ? '#212936' : 'white'),
            maxWidth: '1325px',
        }}>
            <Grid item xs={12}>
                <Typography text="Current purchase data" customStyles={{ fontWeight: 600, mb: 1 }} />
            </Grid>
            <Grid item xs={1.2}>
                <Typography text="Date" customStyles={{ fontSize: '12px', color: '#667085' }} />
                <Typography text={data?.purchased_at || '-'} customStyles={{ fontSize: '14px' }} />
            </Grid>
            <Grid item xs={1.2}>
                <Typography text="Amount" customStyles={{ fontSize: '12px', color: '#667085' }} />
                <Typography text={`$${data?.purchase_price || '0'}`} customStyles={{ fontSize: '14px' }} />
            </Grid>
            <Grid item xs={1.2}>
                <Typography text="Description" customStyles={{ fontSize: '12px', color: '#667085' }} />
                <Typography text={data?.description || '-'} customStyles={{ fontSize: '14px' }} />
            </Grid>
            <Grid item xs={1.2}>
                <Typography text="Venue" customStyles={{ fontSize: '12px', color: '#667085' }} />
                <Typography text={data?.venue || '-'} customStyles={{ fontSize: '14px' }} />
            </Grid>
            <Grid item xs={1.2}>
                <Typography text="Row" customStyles={{ fontSize: '12px', color: '#667085' }} />
                <Typography text={data?.class_2 || '-'} customStyles={{ fontSize: '14px' }} />
            </Grid><Grid item xs={1.2}>
                <Typography text="Section" customStyles={{ fontSize: '12px', color: '#667085' }} />
                <Typography text={data?.class_1 || '-'} customStyles={{ fontSize: '14px' }} />
            </Grid><Grid item xs={1.2}>
                <Typography text="Seats" customStyles={{ fontSize: '12px', color: '#667085' }} />
                <Typography text={data?.seats || '-'} customStyles={{ fontSize: '14px' }} />
            </Grid>
            <Grid item xs={1.2}>
                <Typography text="Purchase ID" customStyles={{ fontSize: '12px', color: '#667085' }} />
                <Typography text={data?.purchase_id || '-'} customStyles={{ fontSize: '14px' }} />
            </Grid>
            <Grid item xs={1.2}>
                <Typography text="Reference ID" customStyles={{ fontSize: '12px', color: '#667085' }} />
                <Typography text={data?.reference_id || '-'} customStyles={{ fontSize: '14px' }} />
            </Grid>
        </Grid>
    );

    return (
        <Modal
            open={open}
            handleClose={onClose}
            title="Split Payment"
            customWidth="min(1400px, 98%)"
        >
            <Box>
                {renderHeader()}

                <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Typography text="Enter quantity" customStyles={{ fontWeight: 500 }} />
                    <TextField
                        value={quantity}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (/^-?\d*\.?\d*$/.test(val)) {
                                setQuantity(val);
                            }
                        }}
                        placeholder="0"
                        sx={{ width: '100px' }}
                    />
                    <Button
                        text="Split"
                        onClick={handleSplit}
                        disabled={!quantity}
                        customStyles={{ height: '40px' }}
                    />
                    {rows.length > 1 && (
                        <IconButton onClick={handleAddRow} color="primary">
                            <AddCircleOutlineIcon />
                        </IconButton>
                    )}
                    <Box flexGrow={1} />
                    <Box display="flex" alignItems="center" gap={2}>
                        <Box>
                            <Typography
                                text={`Total amount: $${rows.reduce((sum, row) => sum + parseFloat(row?.purchase_price || 0), 0).toFixed(2)}`}
                                customStyles={{ fontWeight: 600, }}
                            />
                            {Math.abs(parseFloat(data?.purchase_price || 0) - rows.reduce((sum, row) => sum + parseFloat(row?.purchase_price || 0), 0)) > 0.001 && (
                                <Box>
                                    <Typography
                                        text={`Total entry amount: $${data?.purchase_price}`}
                                        customStyles={{ fontWeight: 600, }}
                                    />
                                    <Typography
                                        text={`Total amount difference: $${(parseFloat(data?.purchase_price || 0) - rows.reduce((sum, row) => sum + parseFloat(row?.purchase_price || 0), 0)).toFixed(2)}`}
                                        customStyles={{
                                            fontWeight: 600,
                                            color: 'red'
                                        }}
                                    />
                                </Box>
                            )}


                        </Box>

                        <Typography
                            text={`Total Rows: ${rows.length}`}
                            customStyles={{ fontWeight: 600 }}
                        />
                    </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2} sx={{ mb: 1, paddingRight: '16px' }}>
                    <Grid item xs={0.5}><Typography text="No" customStyles={{ fontWeight: 500, fontSize: '12px', color: '#667085' }} /></Grid>
                    <Grid item xs={1.2}><Typography text="Date" customStyles={{ fontWeight: 500, fontSize: '12px', color: '#667085' }} /></Grid>
                    <Grid item xs={1.2}><Typography text="Amount" customStyles={{ fontWeight: 500, fontSize: '12px', color: '#667085' }} /></Grid>
                    <Grid item xs={1.2}><Typography text="Description" customStyles={{ fontWeight: 500, fontSize: '12px', color: '#667085' }} /></Grid>
                    <Grid item xs={1.2}><Typography text="Venue" customStyles={{ fontWeight: 500, fontSize: '12px', color: '#667085' }} /></Grid>
                    <Grid item xs={1.2}><Typography text="Section" customStyles={{ fontWeight: 500, fontSize: '12px', color: '#667085' }} /></Grid>
                    <Grid item xs={1.2}><Typography text="Row" customStyles={{ fontWeight: 500, fontSize: '12px', color: '#667085' }} /></Grid>
                    <Grid item xs={1.2}><Typography text="Seats" customStyles={{ fontWeight: 500, fontSize: '12px', color: '#667085' }} /></Grid>
                    <Grid item xs={1.2}><Typography text="Purchase ID" customStyles={{ fontWeight: 500, fontSize: '12px', color: '#667085' }} /></Grid>
                    <Grid item xs={1.2}><Typography text="Reference ID" customStyles={{ fontWeight: 500, fontSize: '12px', color: '#667085' }} /></Grid>
                    <Grid item xs={1.2}></Grid>
                </Grid>

                <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {rows.map((row, index) => (
                        <Grid container spacing={1} key={row.id} alignItems="center" sx={{ mb: 1 }}>
                            <Grid item xs={0.5}>
                                <Typography text={`${index + 1}`} customStyles={{ fontSize: '14px', fontWeight: 500 }} />
                            </Grid>
                            <Grid item xs={1.2}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        value={row.purchased_at ? dayjs(row.purchased_at) : null}
                                        onChange={(date) => handleFieldChange(row.id, 'purchased_at', date ? date.format('YYYY-MM-DD') : '')}
                                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                        sx={classes.datePicker}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={1.2}>
                                <TextField
                                    value={row.purchase_price}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (/^-?\d*\.?\d*$/.test(val)) {
                                            handleFieldChange(row.id, 'purchase_price', val);
                                        }
                                    }}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={1.2}>
                                <TextField
                                    value={row.description || ''}
                                    onChange={(e) => handleFieldChange(row.id, 'description', e.target.value)}
                                    placeholder="Description"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={1.2}>
                                <TextField
                                    value={row.venue || ''}
                                    onChange={(e) => handleFieldChange(row.id, 'venue', e.target.value)}
                                    placeholder="Venue"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={1.2}>
                                <TextField
                                    value={row.class_1 || ''}
                                    onChange={(e) => handleFieldChange(row.id, 'class_1', e.target.value)}
                                    placeholder="Section"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={1.2}>
                                <TextField
                                    value={row.class_2 || ''}
                                    onChange={(e) => handleFieldChange(row.id, 'class_2', e.target.value)}
                                    placeholder="Row"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={1.2}>
                                <TextField
                                    value={row.seats || ''}
                                    onChange={(e) => handleFieldChange(row.id, 'seats', e.target.value)}
                                    placeholder="Seats"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={1.2}>
                                <TextField
                                    value={row.purchase_id || ''}
                                    onChange={(e) => handleFieldChange(row.id, 'purchase_id', e.target.value)}
                                    placeholder="ID"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={1.2}>
                                <TextField
                                    value={row.reference_id || ''}
                                    onChange={(e) => handleFieldChange(row.id, 'reference_id', e.target.value)}
                                    placeholder="Ref ID"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={0.5} display="flex" justifyContent="flex-end">
                                <IconButton onClick={() => handleRemoveRow(row.id)} size="small" color="error">
                                    <DeleteOutlineIcon />
                                </IconButton>
                            </Grid>
                        </Grid>
                    ))}
                </Box>


                <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                        text="Cancel"
                        variant="outlined"
                        onClick={onClose}
                        sx={{
                            fontWeight: '500',
                            fontSize: '14px',
                            color: '#6C727F',
                            border: '1px solid #E5E6EB',
                            textTransform: 'none',
                        }}
                    />
                    <Button
                        text="Save"
                        onClick={handleSave}
                    />
                </Box>
            </Box>


            {/* Warning Dialog */}
            <Dialog
                open={warningOpen}
                onClose={() => setWarningOpen(false)}
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        padding: '16px',
                        maxWidth: '430px',
                        textAlign: 'center',
                    },
                }}
            >
                <Box display="flex" justifyContent="center" mt={1} mb={2}>
                    <Box
                        sx={{
                            backgroundColor: '#FEF0C7',
                            borderRadius: '50%',
                            padding: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '48px',
                            height: '48px',
                            border: '8px solid #FFFAEB',
                        }}
                    >
                        <WarningAmberIcon sx={{ color: '#DC6803', fontSize: '24px' }} />
                    </Box>
                </Box>
                <DialogTitle sx={{ p: 0, mb: 1, fontWeight: 600, fontSize: '18px', color: '#101828' }}>
                    Amount Mismatch
                </DialogTitle>
                <DialogContent sx={{ p: 0, mb: 3 }}>
                    <DialogContentText sx={{ fontSize: '14px', color: '#475467', textAlign: 'center' }}>
                        The total amount of the split rows does not match the original purchase amount.
                        Do you want to proceed with this mismatch?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 0, display: 'flex', gap: '12px', justifyContent: 'center' }}>
                    <Button
                        text="Cancel"
                        variant="outlined"
                        onClick={() => setWarningOpen(false)}
                        sx={{
                            fontWeight: '500',
                            fontSize: '14px',
                            color: '#344054',
                            border: '1px solid #D0D5DD',
                            textTransform: 'none',
                            flex: 1,
                        }}
                    />
                    <Button
                        text="Confirm"
                        onClick={handleConfirmSave}
                        sx={{
                            fontWeight: '500',
                            fontSize: '14px',
                            color: 'white',
                            backgroundColor: '#DC6803',
                            '&:hover': { backgroundColor: '#B54708' },
                            textTransform: 'none',
                            flex: 1,
                        }}
                    />
                </DialogActions>
            </Dialog>
        </Modal>
    );
};

export default SplitPurchaseModal;
